<template>
  <div class="share-modal overlay overlay--grow" 
    v-show="shareModalVisible"
  >
    <transition name="slideDown">
      <div>
        <div 
          ref="target" 
          class="overlay__inner" 
          v-show="shareModalVisible" 
          @click.stop="closeAutoSuggestions()"
        >
          <div class="pricing-row-config">
            <button class="btn btn-icon pricing-row-config__close" aria-label="close"
              @click.prevent="toggleModal"
            ><i class="fa fa-close"></i></button>
            <h3 class="pricing-row-config__head pricing-row-config__head--left pricing-row-config__head--main pricing-row-config__head--drk"
              @mouseenter="moveable.draggable = true"
              @mouseleave="moveable.draggable = false"
            >Share</h3> 
            <div>

              <form class="share-modal__form" autocomplete="off">
                <div class="share-modal__inputs">

                  <div class="inputs__group">
                    <label>To:</label>
                    <input 
                      type="text" 
                      v-model="emailTo" 
                      v-on:keyup="handleKeyPress($event, 'to')" 
                      placeholder="Use comma to separate email addresses"
                      required
                    >
                    <div class="share-modal__button">
                      <button type="button" v-bind:disabled="!canSubmit()" @click="submit()"><i class="fa fa-circle-o-notch fa-spin" v-show="isSubmitting"></i> Send</button>
                    </div>
                  </div>
                  <div
                    class="share-modal__autosuggest" 
                    v-show="emailTo && emailTo.length > 0 && ! hasAllValidToEmails()"
                  >
                    <ul>
                      <li 
                        v-for="suggestion in getFilteredSuggestions('to')"
                        v-bind:class="{selected: suggestion == selectedSuggestion, disabled: emailTos.includes(suggestion) !== false}"
                      >
                        <span role="button" class="suggestion__label" @click.stop="addToEmail(suggestion)">{{ suggestion }}</span>
                        <button type="button" class="suggestion__delete" @click.stop="deleteSavedEmail(suggestion)">&times;</button>
                      </li>
                    </ul>
                  </div>

                  <div class="inputs__group">
                    <label>Cc:</label>
                    <input 
                      type="text" 
                      v-model="emailCc" 
                      v-on:keyup="handleKeyPress($event, 'cc')"
                      placeholder="Use comma to separate email addresses"
                    >
                  </div>
                  <div
                    class="share-modal__autosuggest" 
                    v-show="emailCc && emailCc.length > 0 && ! hasAllValidCcEmails()"
                  >
                    <ul>
                      <li 
                        v-for="suggestion in getFilteredSuggestions('cc')"
                        v-bind:class="{selected: suggestion == selectedSuggestion, disabled: emailCcs.includes(suggestion) !== false}"
                      >
                        <span role="button" class="suggestion__label" @click.stop="addCcEmail(suggestion)">{{ suggestion }}</span>
                        <button type="button" class="suggestion__delete" @click.stop="deleteSavedEmail(suggestion)">&times;</button>
                      </li>
                    </ul>
                  </div>

                  <div class="inputs__group">
                    <label>Subject:</label>
                    <input type="text" v-model="emailSubject" required>
                  </div>
                  <div class="inputs__group">
                    <label>Message:</label>
                    <div class="inputs__message-wrap">
                      <label><i class="pacific">{{ emailBodyCharsRemaining }} characters remaining</i></label>
                      <textarea v-model="emailBody" v-bind:maxlength="emailBodyMaxChars" required></textarea>
                    </div>
                  </div>
                </div>

                <div class="share-modal__screenshot">
                  <label>Preview of image <i>(set browser view to 100% for best image)</i></label>
                  <div v-if="!screenshot" class="screenshot">
                    <i class="fa fa-circle-o-notch fa-spin fa-3x"></i>
                  </div>
                  <div 
                    v-else 
                    class="screenshot" 
                  >
                    <img v-bind:src="screenshot" alt="">
                  </div>
                </div>
              </form>

            </div>
          </div>
        </div>

        <Moveable
          className="moveable"
          :target="$refs.target"
          :draggable="moveable.draggable"
          :origin="false"
          :hideDefaultLines="true"
          @drag="handleDrag"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import Moveable from 'vue3-moveable'
import moment from 'moment'
import { formatNumber } from '../../../../common/helpers'

export default {
  props: ['shareModalVisible', 'requestId', 'term', 'date', 'componentId', 'chartLockedDate'],
  components: {
    Moveable,
  },
  data() {
    return {
      emailTo: null,
      emailCc: null,
      emailBody: null,
      emailSubject: null,
      emailBodyMaxChars: 1500,
      isSubmitting: false,
      selectedSuggestion: null,
      moveable: {
        draggable: false,
      }
    }
  },
  computed: {
    suggestions() {
      return this.$store.getters.userShares
    },
    emailTos() {
      if (! this.emailTo) {
        return []
      }

      let tos = this.emailTo.split(',')
      if (! tos) {
        return []
      }

      return tos.map(email => email.trim()).filter(email => {
        return this.isValidEmail(email)
      })
    },
    emailCcs() {
      if (! this.emailCc) {
        return []
      }

      let ccs = this.emailCc.split(',')
      if (! ccs) {
        return []
      }

      return ccs.map(email => email.trim()).filter(email => {
        return this.isValidEmail(email)
      })
    },
    emailBodyCharsRemaining() {
      if (this.emailBody) {
        return formatNumber(this.emailBodyMaxChars - this.emailBody.length)
      }
      return formatNumber(this.emailBodyMaxChars)
    },
    userFirstName() {
      if (this.isLoggedIn()) {
        return this.$store.state.auth.user.first_name
      }
    },
    userEmail() {
      if (this.isLoggedIn()) {
        return this.$store.state.auth.user.email
      }
    },
    facilityProfileName() {
      return this.$store.getters.facilityProfileName
    },
    screenshot() {
      return this.$store.state.viewCapture
    }
  },
  mounted() {
    this.emailCc = this.userEmail
    this.emailSubject = this.getEmailSubjectString()
  },
  watch: {
    term() {
      this.emailSubject = this.getEmailSubjectString()
    }
  },
  methods: {
    /**
     * Get the default email subject
     * 
     * @return {String}
     */
    getEmailSubjectString() {
      if (this.facilityProfileName) {
        return 'All-In Price Monitor _ ' + this.facilityProfileName + ' _ ' + this.term + 'mo'
      }
      return 'All-In Price Monitor _ ' + this.term + 'mo'
    },
    /**
     * Clean a string of email address into a
     * comma-separated list.
     *
     * @param  {String}  emailString
     * @return {String}
     */
    cleanEmailString(emailString) {
      if (! emailString) {
        return ''
      }
      emailString = emailString.replace(/\s\s+/g, " ")
      emailString = emailString.replace("", ",")
      emailString = emailString.replace(" ", ",")

      let emails = emailString.split(',')
      let valids = emails.map(email => email.trim()).filter(email => {
        return this.isValidEmail(email)
      })
      return valids.join(', ')
    },
    /**
     * Add a "to" email address.
     *
     * @param  {String}  text
     * @return void
     */
    addToEmail(text) {
      if (this.isValidEmail(text)) {
        let existing = this.cleanEmailString(this.emailTo)
        if (existing) {
          this.emailTo = existing + ', ' + text
        } else {
          this.emailTo = text
        }
      }
    },
    /**
     * Add a "cc" email address.
     *
     * @param  {String}  text
     * @return void
     */
    addCcEmail(text) {
      if (this.isValidEmail(text)) {
        let existing = this.cleanEmailString(this.emailCc)
        if (existing) {
          this.emailCc = existing + ', ' + text
        } else {
          this.emailCc = text
        }
      }
    },
    /**
     * Delete a saved email address.
     *
     * @param  {String}  email
     * @return void
     */
    deleteSavedEmail(email) {
      this.$store.dispatch('removeFromUserShareEmails', email)
    },
    /**
     * Determine if a string is a valid email address.
     *
     * @param  {String}  text
     * @return {Boolean}
     */
    isValidEmail(text) {
      return /\S+@\S+\.\S+/.test(text)
    },
    /**
     * Determine if the entered "to" input 
     * contains all valid email addresses.
     *
     * @return {Boolean}
     */
    hasAllValidToEmails() {
      if (! this.emailTo) {
        return false
      }

      let tos = this.emailTo.split(',')
      if (! tos) {
        return false
      }

      for (let i = 0; i < tos.length; i++) {
        if (! this.isValidEmail(tos[i].trim())) {
          return false
        }
      }
      return true
    },
    /**
     * Determine if the entered "cc" input 
     * contains all valid email addresses.
     *
     * @return {Boolean}
     */
    hasAllValidCcEmails() {
      if (! this.emailCc) {
        return false
      }

      let ccs = this.emailCc.split(',')
      if (! ccs) {
        return false
      }

      for (let i = 0; i < ccs.length; i++) {
        if (! this.isValidEmail(ccs[i].trim())) {
          return false
        }
      }
      return true
    },
    /**
     * Close the auto-suggestions lists by cleaning both email inputs.
     */
    closeAutoSuggestions() {
      this.emailTo = this.cleanEmailString(this.emailTo)
      this.emailCc = this.cleanEmailString(this.emailCc)
    },
    /**
     * Get an array of email address suggestions
     * based on the entered text in the "to" or "cc" input.
     *
     * @param  {String}  which
     * @return {Array}
     */
    getFilteredSuggestions(which) {
      let emails

      switch (which) {
        case 'to':
          emails = this.emailTo
          break
        case 'cc':
          emails = this.emailCc
          break
      }

      if (! emails) {
        return []
      }

      // Pull out the only invalid email address (the one currently being typed)
      // and find suggestions.
      let tos = emails.split(',')
      let invalids = tos.map(email => email.trim()).filter(email => {
        return ! this.isValidEmail(email)
      })

      if (invalids && invalids.length > 0) {
        return this.suggestions.filter(option => {
          let invalid = invalids[0].toLowerCase()
          if (invalid != "") {
            return option.toLowerCase().startsWith(invalid)
          }
        })
      }
      return []
    },
    /**
     * Handle a keypress in the "to" or "cc" input.
     *
     * @param  {Event}   e
     * @param  {String}  which
     */
    handleKeyPress(e, which) {
      // Convert space or semicolon to ", "
      if (e.key == " " || e.key == ";") {
        switch (which) {
          case 'to':
            this.emailTo = this.cleanEmailString(this.emailTo) + ', '
            break
          case 'cc':
            this.emailCc = this.cleanEmailString(this.emailCc) + ', '
            break
        }
      }

      // Auto suggest
      let filteredSuggestions = [];
      switch (which) {
        case 'to':
          filteredSuggestions = this.getFilteredSuggestions('to')
          break
        case 'cc':
          filteredSuggestions = this.getFilteredSuggestions('cc')
          break
      }

      if (filteredSuggestions.length <= 0) {
        return
      }

      if (this.selectedSuggestion) {
        let currentIndex = filteredSuggestions.findIndex(suggestion => {
          return suggestion == this.selectedSuggestion
        })

        let index

        switch (e.key) {
          case 'ArrowUp':
            if (currentIndex == 0) {
              index = filteredSuggestions.length - 1
            } else {
              index = currentIndex - 1
            }
            this.selectedSuggestion = filteredSuggestions[index]
            break
          case 'ArrowDown':
            if (currentIndex < filteredSuggestions.length - 1) {
              index = currentIndex + 1
            } else {
              index = 0
            }
            this.selectedSuggestion = filteredSuggestions[index]
            break
          case 'Enter':
            switch (which) {
              case 'to':
                if (this.emailTos.includes(this.selectedSuggestion) === false) {
                  this.addToEmail(this.selectedSuggestion)
                }
                break
              case 'cc':
                if (this.emailCcs.includes(this.selectedSuggestion) === false) {
                  this.addCcEmail(this.selectedSuggestion)
                }
                break
            }
            this.selectedSuggestion = null
            break
        }
      } else {
        switch (e.key) {
          case 'ArrowUp':
            this.selectedSuggestion = filteredSuggestions[filteredSuggestions.length - 1]
            break
          case 'ArrowDown':
            this.selectedSuggestion = filteredSuggestions[0]
            break
        }
      }
    },
    /**
     * Determine if the requirements to submit the 
     * form have been met.
     *
     * @return {Boolean}
     */
    canSubmit() {
      // Currently posting to server?
      if (this.isSubmitting) {
        return false
      }

      // Entered a subject at least three chars in length?
      if (! this.emailSubject || this.emailSubject.length < 3) {
        return false
      }

      // Entered valid "To" email addresses?
      if (! this.hasAllValidToEmails()) {
        return false
      }

      return true
    },
    /**
     * Determine if current user is logged-in.
     *
     * @return {Boolean}
     */
    isLoggedIn() {
      return this.$store.state.auth && this.$store.state.auth.user
    },
    /**
     * Toggle the modal's visibility.
     *
     * @return {void}
     */
    toggleModal() {
      this.$refs.target.setAttribute('style', 'transform: matrix(1,0,0,1,0,0) translate(0, 0)')
      this.$emit('toggleModal')
    },
    /**
     * Submit the form.
     *
     * @return {void}
     */
    submit() {
      this.isSubmitting = true;

      axios.post('/pricing-calculator/share', { 
        id: this.requestId,
        to: this.emailTo,
        cc: this.emailCc,
        subject: this.emailSubject,
        body: this.emailBody,
        term: this.term,
        date: this.date,
        componentId: this.componentId,
        lockedDate: this.chartLockedDate ? moment(this.chartLockedDate, 'M/DD/YYYY').format('YYYY-MM-DD') : null,
      })
        .then(response => {
          this.$store.dispatch('addToUserShareEmails', this.emailTos)
          this.isSubmitting = false
          this.selectedSuggestion = null
          this.emailTo = null
          this.emailBody = null
          this.toggleModal()

          swal({
            text: 'Your information was shared successfully.',
            animation: false,
            closeOnEsc: false,
            closeOnClickOutside: false,
          })
        })
        .catch(error => {
          this.isSubmitting = false

          swal({
            text: 'An unknown error occurred.',
            animation: false,
            closeOnEsc: false,
            closeOnClickOutside: false,
          })
        })
    },
    /**
     * Handle the form modal dragging.
     *
     * @return {void}
     */
    handleDrag({ target, transform }) {
      let ogTransform = target.style.transform

      target.style.transform = transform

      let bounding = target.getBoundingClientRect();
      if (bounding.top < 0) {
        target.style.transform = ogTransform
      }
    },
  }
}
</script>
